export type TCognitionDocumentType = SafeExtract<TDocumentType, 'moca' | 'mmse' | 'tmt' | 'cdr' | 'sdmt' | 'cerad'>;

type TSecondaryDocumentType = SafeExtract<TDocumentType, 'background'>;

export type TRelevantDocumentType = TCognitionDocumentType | TSecondaryDocumentType;

export const getHeaderId = (
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>,
  editing?: string,
  viewing?: string,
): string => {
  if (!editing && !viewing) return 'cognition.title';
  // handle both editing and viewing modes
  const docId = editing ?? viewing;
  // if the user is in editing or viewing mode and docType returns the doctype, return correct title for the header

  if (docId) {
    const type = documents.find(d => d._id === docId)?._type;
    if (type) {
      switch (type) {
        case 'cerad':
          // Implement header selection based on version if/when needed, for now default to 2023
          return 'cognition.cerad.cerad2023';
        default:
          return `cognition.${type}.${type}`;
      }
    }
  }
  return 'cognition.title';
};

export const getColorOfDigitPair = (
  practiceAnswers: Array<{ index: number; correctOrFalse: 'correct' | 'false' }>,
  practiceQuestionsAnswered: number,
  index: number,
): 'white' | 'green' | 'red' | 'blue' => {
  if (practiceQuestionsAnswered === index) {
    return 'blue';
  }
  if (practiceAnswers[index]?.correctOrFalse === 'false') {
    return 'red';
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (practiceAnswers[index]?.correctOrFalse === 'correct') {
    return 'green';
  }
  return 'white';
};

/**
 * Function for deciding whether to render symboldigitpair or not in the test
 * @param testQuestionsAnswered number of questions answered
 * @param index index of current symboldigitpair in the array
 */
export const renderSdmtRow = (testQuestionsAnswered: number, index: number): boolean => {
  const config = [
    { min: 0, max: 4 },
    { min: 5, max: 19 },
    { min: 20, max: 34 },
    { min: 35, max: 49 },
    { min: 50, max: 64 },
    { min: 65, max: 79 },
    { min: 80, max: 94 },
    { min: 95, max: 109 },
  ];
  const currentRange =
    testQuestionsAnswered === 0
      ? { min: 0, max: 4 }
      : config.find(
          (obj: { min: number; max: number }) => testQuestionsAnswered >= obj.min && testQuestionsAnswered <= obj.max,
        );
  if (currentRange && index >= currentRange.min && index <= currentRange.max) {
    return true;
  }
  /*
  const temp = Math.floor(testQuestionsAnswered / 10) * 10;
  if (index >= temp && index < temp + 10) {
    return true;
  }
  */
  return false;
};

/**
 * function for deciding whether to render a row or not. If user is not in viewing mode (is in editing mode), render every row
 * @param doc formData.document
 * @param viewing boolean that tells if user is in viewing mode
 * @param name name of the row/question
 */
export const renderTmtRow = (doc: ITMT, viewing: boolean | undefined, name: string): boolean => {
  if (!viewing) {
    return true;
  } else {
    if (name === 'tmtaTime') {
      if (doc.tmtaUnableToPerform?.[0] === 'unableToPerform') {
        return false;
      }
    }
    if (name === 'tmtbTime') {
      if (doc.tmtbUnableToPerform?.[0] === 'unableToPerform') {
        return false;
      }
    }
    if (name === 'tmtaUnableToPerform') {
      if (doc.tmtaUnableToPerform?.[0] != 'unableToPerform') {
        return false;
      }
    }
    if (name === 'tmtbUnableToPerform') {
      if (doc.tmtbUnableToPerform?.[0] != 'unableToPerform') {
        return false;
      }
    }
  }
  return true;
};
